import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchGame, joinGame, rejoinGame } from '../api/games';
import { saveSession } from '../api/session';
import { PageShell } from '../components/PageShell';
import { Form } from '../components/form/Form';
import { Button } from '../components/button/Button';

type Step = 'code' | 'name' | 'rejoin';

export default function JoinPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>('code');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleCodeSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const data = await fetchGame(code);
      if (data.state === 'lobby') {
        setStep('name');
      } else {
        setStep('rejoin');
      }
    } catch {
      setError('Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const membership = await joinGame(code, name, pin);
      saveSession({ code, playerName: membership.player, token: membership.token });
      nav(`/games/${code}/play`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Join failed');
      setLoading(false);
    }
  };

  const handleRejoin: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const membership = await rejoinGame(code, name, pin);
      saveSession({ code, playerName: membership.player, token: membership.token });
      nav(`/games/${code}/play`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Recovery failed');
      setLoading(false);
    }
  };

  return (
    <PageShell>
      {step === 'code' && (
        <Form
          title="Join Game"
          disabled={!code}
          loading={loading}
          onSubmit={handleCodeSubmit}
          submitLabel="Submit"
        >
          <label className="label">
            Code
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              maxLength={6}
              required
              placeholder="ABC123"
            />
          </label>
        </Form>
      )}

      {step === 'name' && (
        <Form
          title={`Game ${code}`}
          subtitle="Use your real name — players need to identify you"
          disabled={!name || pin.length !== 4}
          loading={loading}
          onSubmit={handleJoin}
          submitLabel="Join"
        >
          <label className="label">
            Name
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="name"
              required
            />
          </label>
          <label className="label">
            Recovery PIN
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4 digits"
              inputMode="numeric"
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setError(null);
              setStep('rejoin');
            }}
          >
            Already joined? Recover access
          </Button>
        </Form>
      )}
      {step === 'rejoin' && (
        <Form
          title={`Game ${code}`}
          subtitle="Recover your access with your name and PIN"
          disabled={!name || pin.length !== 4}
          loading={loading}
          onSubmit={handleRejoin}
          submitLabel="Recover"
        >
          <label className="label">
            Name
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="label">
            PIN
            <input
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="4 digits"
              inputMode="numeric"
              required
            />
          </label>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              setError(null);
              setStep('code');
            }}
          >
            Back
          </Button>
        </Form>
      )}
      {error && <p className="error">{error}</p>}
    </PageShell>
  );
}
